/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

// tslint:disable:no-non-null-assertion

import { Component, OnInit } from '@angular/core';
import {
  BuilderPermission,
  ContactDetailsInvitationSubTabDto,
  ContactInvitationStatus,
  ContactType,
} from '@gh/builder-shared-data';
import { ContextRoute, getContextRouteInitData, getContextRouteStaticData } from '@gh/common-internal-shared';
import { FormState, FormStateBuilder, HelpID, UserContext } from '@gh/common-shared';
import { MessageBasket } from '@gh/core-messages';
import { DialogService, FormMode, NotificationService, WaitingService, yesAnswer, yesNoConfirmation } from '@gh/core-ui';
import { assign } from 'lodash';
import { ContactDetailsBusinessService } from '../../resources/contact-details-business.service';
import { ContactDetailsFormState } from '../contact-details-tab-container/contact-details-form.service';
import { ContactDetailsModel } from '../../services/contact-details-model.service';
import { ContactDetails } from '../../resources/model/contact-details.model';
import { ValidatorFn, Validators } from '@angular/forms';
import { observableBasedSlicedDataSource, SlicedDataSource } from '@gh/core-data';
import { of$ } from '@gh/rx';
import { GridDefinitionFactory, LayoutBasedGridDefinition, newGridPaginationCursor } from '@gh/core-ui-grid';
import { filter$ } from '@gh/rx/operators';

@Component({
  selector: 'gh-contact-details-shared-portal-tab.component',
  template: require('./contact-details-shared-portal-tab.component.html'),
  styles: [require('./contact-details-shared-portal-tab.scss')],
})
export class ContactDetailsSharedPortalTabComponent implements OnInit {
  form: FormState<{ email: string }>;
  cursor = newGridPaginationCursor();
  dataSource: SlicedDataSource<ContactDetailsInvitationSubTabDto>;
  gridDefinition: LayoutBasedGridDefinition;
  tab: string;
  type: ContactType;
  helpId: HelpID;
  contactDetails: ContactDetails;
  MAX_USERS = 3;

  constructor(
    private userContext: UserContext,
    private notifications: NotificationService,
    private rootForm: ContactDetailsFormState,
    private route: ContextRoute,
    private formStateBuilder: FormStateBuilder,
    private waitings: WaitingService,
    private dialogService: DialogService,
    private businessService: ContactDetailsBusinessService,
    public gridDefinitionFactory: GridDefinitionFactory,
    public model: ContactDetailsModel,
  ) {
  }

  ngOnInit(): void {
    const { gridLayout } = getContextRouteInitData(this.route);
    this.contactDetails = this.model.original;
    this.type = ContactType[getContextRouteStaticData(this.route)['type']];
    this.tab = getContextRouteStaticData(this.route)['tab'];
    this.createDataSource();
    this.form = this.formStateBuilder
                    .buildPlain({
                                  value: void 0,
                                  mode: FormMode.EditMode,
                                  enabled: true,
                                  children: {
                                    email: {
                                      required: true,
                                      validators: [
                                        Validators.required,
                                        this.emailCountValidator(this.MAX_USERS),
                                      ],
                                    },
                                  },
                                });
    this.gridDefinition = this.gridDefinitionFactory.buildLayoutBased(
      {
        invitationStatus: {
          cellClass: ({ data }: { data: ContactDetailsInvitationSubTabDto }) => ({
            [ContactInvitationStatus.Invited]: 'cell--important',
            [ContactInvitationStatus.Deactivated]: 'cell--negative',
            [ContactInvitationStatus.Activated]: 'cell--positive',
          }[data.invitationStatus]),
        },
      },
      { getRowNodeId:  (data: ContactDetailsInvitationSubTabDto) => data.invitationId},
      );
    this.gridDefinition.bind(gridLayout);
    // @ts-ignore
    this.helpId = {
      [ContactType.Customer]: 'contacts/details/customer/portal',
      [ContactType.CreditorSupplier]: 'contacts/details/creditor-supplier/portal',
    }[this.type] || '';
    assign(this, getContextRouteInitData(this.route));
  }

  get hasPermissionForContactType(): boolean {
    return this.userContext.hasPermission(this.type === ContactType.Customer ?
      BuilderPermission.Contacts.Customer.CREATE_EDIT
      : BuilderPermission.Contacts.CreditorSupplier.CREATE_EDIT);
  }

  get emailListForInvitations(): string[] | any[] {
    const listOfOthers = this.contactDetails.common.otherEmails ?
      this.contactDetails.common.otherEmails?.split(';') : [];
    return [this.contactDetails.common?.email, ...listOfOthers]
             .filter((email: string) =>
                       !this.invitations?.find(
                         (invitation: ContactDetailsInvitationSubTabDto) => invitation.invitationEmail === email,
                       )) || [];
  }

  get invitations(): ContactDetailsInvitationSubTabDto[] | undefined {
      switch (this.type) {
        case ContactType.Customer:
          return this.contactDetails.customer?.invitations;
        case ContactType.CreditorSupplier:
          return this.contactDetails.creditorSupplier?.invitations;
        default:
          return [];
      }
  }

  createDataSource(): void {
    this.dataSource = observableBasedSlicedDataSource(of$(this.invitations || []));
  }

  updateModel(): void {
    this.businessService.get(this.rootForm.value.id!).subscribe((res) => {
      this.model.setOriginal(res);
      this.contactDetails = res;
      this.form.getControl('email').reset();
      this.createDataSource();
    });
  }

  invite(email: string): void {
    this.businessService
      .inviteToPortal(this.rootForm.value.id!, email, this.type).pipe(
      this.waitings.operator())
      .subscribe(() => this.updateModel(),
                 (error: MessageBasket) => this.notifications.processAll(error),
      );
  }

  onResendInvitation(invitation: ContactDetailsInvitationSubTabDto): void {
    this.businessService
        .resendInvite(this.rootForm.value.id!, invitation, this.type).pipe(
      this.waitings.operator())
        .subscribe(
          () => this.updateModel(),
          (error: MessageBasket) => this.notifications.processAll(error),
        );
  }

  onDeleteInvitation(invitation: ContactDetailsInvitationSubTabDto): void {
    this.dialogService.confirmation(
      yesNoConfirmation('msg_delete_customer_account', {email: invitation.invitationEmail })).pipe(
      filter$(yesAnswer)).subscribe(() => {
      this.businessService
          .deleteInvitation(this.rootForm.value.id!, invitation, this.type).pipe(
        this.waitings.operator()).subscribe(
        () => this.updateModel(),
        (error: MessageBasket) => this.notifications.processAll(error),
      );
    });
  }

   emailCountValidator(max: number): ValidatorFn {
    return (): { [key: string]: any } | null => {
      const invalid = this.invitations &&  this.invitations.filter(
        (invite) => invite.invitationStatus === ContactInvitationStatus.Activated ||
                    invite.invitationStatus === ContactInvitationStatus.Invited).length >= max;
      // tslint:disable-next-line:no-null-keyword
      return invalid ? { maxUserCount: {count: this.MAX_USERS} } : null;
    };
  }

  get isEditMode(): boolean {
    return  this.rootForm.editMode;
  }

  get hasUserCountError(): boolean {
    const errors = this.form.group.controls['email'].errors;
    return errors && errors['maxUserCount'];
  }
}
